import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "../api";
import {
  ClipboardList, CheckCircle2, Circle, Calendar, MapPin, User, Mail,
  Loader2, Upload, FileText, Briefcase, PartyPopper,
} from "lucide-react";

export default function StudentOnboarding() {
  const fileRef = useRef(null);
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.get("/onboarding/mine");
      setRec(r.data);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleTask = async (task) => {
    setBusy(true);
    try {
      await api.patch(`/onboarding/${rec.id}/tasks/${task.id}`, { done: !task.done });
      await load();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "other");
    setBusy(true); setErr("");
    try {
      await api.post(`/onboarding/${rec.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <div className="card card-pad text-sm text-muted flex items-center gap-2" data-testid="student-onb-loading">
        <Loader2 size={14} className="animate-spin" /> Loading onboarding…
      </div>
    );
  }

  if (!rec) {
    return (
      <div className="card card-pad text-center" data-testid="student-onb-none">
        <ClipboardList size={26} className="mx-auto text-muted mb-2" />
        <div className="font-semibold">No onboarding yet</div>
        <p className="text-xs text-muted mt-1 max-w-md mx-auto">
          When you accept an offer from a company, your onboarding checklist will appear here.
        </p>
        <Link to="/student/postings" className="btn-primary mt-4 inline-flex items-center gap-2">
          <Briefcase size={14} /> Browse jobs
        </Link>
      </div>
    );
  }

  const tasks = rec.tasks || [];
  const documents = rec.documents || [];
  const done = tasks.filter((t) => t.done).length;
  const progress = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="student-onboarding">
      <div className="rounded-sm p-6 text-white bg-brand-gradient" data-testid="student-onb-hero">
        <span className="eyebrow on-grad-muted inline-flex items-center gap-1.5"><ClipboardList size={11} /> Onboarding</span>
        <h1 className="font-display font-extrabold text-2xl md:text-3xl mt-1">
          Welcome to {rec.company_name}!
        </h1>
        <p className="text-sm text-white/80 mt-1">{rec.posting_title}</p>
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-white/85">
          {rec.joining_date && <span className="inline-flex items-center gap-1.5"><Calendar size={12} /> Joining {rec.joining_date}{rec.reporting_time ? ` · ${rec.reporting_time}` : ""}</span>}
          {rec.work_location && <span className="inline-flex items-center gap-1.5"><MapPin size={12} /> {rec.work_location}</span>}
          {rec.reporting_manager && <span className="inline-flex items-center gap-1.5"><User size={12} /> {rec.reporting_manager}</span>}
          {rec.manager_email && <span className="inline-flex items-center gap-1.5"><Mail size={12} /> {rec.manager_email}</span>}
        </div>
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{err}</div>}

      {rec.status === "completed" ? (
        <div className="card card-pad bg-emerald-50 border-emerald-200 text-center" data-testid="student-onb-completed">
          <PartyPopper size={32} className="mx-auto text-emerald-700 mb-2" />
          <div className="font-display font-extrabold text-xl">You&apos;re officially onboarded!</div>
          <p className="text-sm text-emerald-900 mt-1">See you on day one — good luck.</p>
        </div>
      ) : (
        <div className="card card-pad">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-extrabold text-lg flex items-center gap-2"><CheckCircle2 size={16} /> Your checklist</h2>
            <span className="text-xs text-muted">{done}/{tasks.length} done · {progress}%</span>
          </div>
          <div className="h-2 rounded-full bg-paper overflow-hidden mb-4" aria-label="Progress">
            <div className="h-full bg-signal transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
          <ul className="space-y-1.5">
            {tasks.map((t) => (
              <li key={t.id} className="flex items-center gap-3 p-2 rounded-md hover:bg-paper transition" data-testid={`student-task-${t.id}`}>
                <button
                  type="button"
                  onClick={() => toggleTask(t)}
                  disabled={busy}
                  aria-label={t.done ? "Mark incomplete" : "Mark complete"}
                  data-testid={`student-task-toggle-${t.id}`}
                  className="shrink-0 disabled:opacity-50"
                >
                  {t.done
                    ? <CheckCircle2 size={20} className="text-emerald-600" />
                    : <Circle size={20} className="text-muted" />}
                </button>
                <div className="min-w-0 flex-1">
                  <div className={`text-sm ${t.done ? "line-through text-muted" : ""}`}>{t.label}</div>
                </div>
                {t.required && (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Required</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card card-pad">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-extrabold text-lg flex items-center gap-2"><FileText size={16} /> Documents ({documents.length})</h2>
          {rec.status !== "completed" && (
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="btn-secondary inline-flex items-center gap-1.5 text-xs" data-testid="student-onb-upload">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx"
            onChange={(e) => uploadDoc(e.target.files?.[0])}
          />
        </div>
        {documents.length === 0 ? (
          <div className="text-sm text-muted text-center py-4">No documents yet. Upload any IDs / signed docs your HR asked for.</div>
        ) : (
          <ul className="space-y-2">
            {documents.map((d) => (
              <li key={d.id} className="flex items-center gap-3 p-2 rounded-md border" style={{ borderColor: "var(--rule)" }}>
                <FileText size={16} className="text-signal shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{d.original_filename}</div>
                  <div className="text-[11px] text-muted">{(d.size / 1024).toFixed(1)} KB · {new Date(d.uploaded_at).toLocaleString()}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
